import React, { useState } from 'react';
import { Modal, View, Text, TextInput, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';

import { useAppTheme } from '../../design-system/ThemeProvider';
import { t } from '../../i18n/translations';
import { supabase } from '../../shared/services/supabaseService';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { useLanguageStore } from '../quiz-core/languageStore';
import { uiText } from '../../shared/utils/uiText';

interface CreateGroupModalProps {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ visible, onClose, onCreated }) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  const [familyName, setFamilyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!familyName.trim()) {
      setError(t('familiesFriends.errorFamilyNameRequired', language));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      
      const { data, error } = await supabase.rpc('create_family', {
        p_name: familyName,
      });
      console.log('RPC result:', data, error);
      if (error) throw error;
      setLoading(false);
      setFamilyName('');
      onCreated();
      onClose();
    } catch (e: unknown) {
      setLoading(false);
      const message = e instanceof Error ? e.message : '';
      if (message.includes('family_limit_reached')) {
        setError(t('familiesFriends.errorFamilyLimit', language));
      } else {
        setError(message || 'Failed to create family.');
      }
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: colors.surfaceContainerLow, borderColor: colors.outlineVariant, borderWidth: 1 }]}>
          <Text style={[styles.title, { color: colors.onSurface }]}>{uiText('createGroupTitle')}</Text>
          <TextInput
            placeholder={t('familiesFriends.placeholderGroupName', language)}
            value={familyName}
            onChangeText={setFamilyName}
            style={[styles.input, { backgroundColor: colors.surfaceContainerHigh, color: colors.onSurface, borderColor: colors.outlineVariant }]}
            placeholderTextColor={colors.onSurfaceVariant}
            editable={!loading}
          />
          {error && <Text style={styles.error}>{error}</Text>}
          {loading ? (
            <ActivityIndicator color="#3381c6" />
          ) : (
            <PrimaryButton title={t('familiesFriends.buttonCreate', language)} onPress={handleCreate} />
          )}
          <TouchableOpacity onPress={onClose} disabled={loading} style={styles.cancelBtn}>
            <Text style={[styles.cancelText, { color: colors.primary }]}>{t('familiesFriends.buttonCancel', language)}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    borderRadius: 18,
    padding: 28,
    width: '85%',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 18,
  },
  input: {
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    fontSize: 16,
    width: '100%',
    marginBottom: 12,
  },
  error: {
    color: '#ff6b6b',
    marginBottom: 8,
    textAlign: 'center',
  },
  cancelBtn: {
    marginTop: 10,
  },
  cancelText: {
    fontSize: 16,
  },
});

export default CreateGroupModal;
